/* global DetectUserAgent */
import React from "react";
import PropTypes from "prop-types";
import { isMobileOnly } from "react-device-detect";
import { FacebookShareButton } from "react-share";
import { Link, Typography } from "@mui/material";

import { useWebShare, useNotifications, useScreen, useAlgoliaEvents, useClipboard } from "hooks";
import MakePresenter from "presenters/MakePresenter";
import TemplateGroupPresenter from "presenters/TemplateGroupPresenter";
import FacebookIcon from "assets/images/share-buttons/share-facebook.png";
import EmailIcon from "assets/images/share-buttons/share-email.png";
import LinkIcon from "assets/images/share-buttons/share-link.png";
import MoreIcon from "assets/images/share-buttons/share-more.png";
import SMSIcon from "assets/images/share-buttons/share-sms.png";
import ResponsiveImage from "components/ResponsiveImage";
import FacebookInAppShare from "components/FacebookInAppShare";
import { trackShare } from "tracking/google/analytics";
import { SHARE_METHODS } from "enums";

import useStyles from "./useStyles";

const ShareOptions = (props) => {
  const {
    onClose,
    onEmailFormOpen,
    make,
    message: { text, url },
    autoShowCVPModal,
    templateGroup,
  } = props;
  const classes = useStyles();
  const { addErrorNotification } = useNotifications();
  const { trackEventAfterSearchAndShare } = useAlgoliaEvents();
  const { copy } = useClipboard();
  const inApp = DetectUserAgent.isTikTokBrowser() || DetectUserAgent.isInstagramBrowser();

  const trackFacebookShare = () => {
    trackShare(SHARE_METHODS.FACEBOOK, templateGroup);
    trackEventAfterSearchAndShare(make);
  };

  const handleFacebookShare = () => {
    trackFacebookShare();
    onClose();
  };

  const handleSmsShare = () => {
    trackShare(SHARE_METHODS.SMS, templateGroup);
    trackEventAfterSearchAndShare(make);
  };

  const handleCopyToClipboard = () => {
    trackShare(SHARE_METHODS.LINK, templateGroup);
    trackEventAfterSearchAndShare(make);
    copy(url, "Link copied to clipboard")
      .then(() => {
        autoShowCVPModal();
        onClose();
      })
      .catch(() => addErrorNotification());
  };

  const handleWebShare = () => {
    autoShowCVPModal();
    onClose();
  };

  const { isWebShareSupported, share } = useWebShare(handleWebShare);
  const { isDesktop } = useScreen();

  const handleShare = () => {
    trackShare(SHARE_METHODS.BROWSER, templateGroup);
    trackEventAfterSearchAndShare(make);
    share({ url, text });
  };

  return (
    <div className={classes.options} data-testid="shareoptions">
      {inApp ? (
        <FacebookInAppShare classes={classes} url={url} handleTracking={trackFacebookShare} />
      ) : (
        <FacebookShareButton
          quote={text}
          url={url}
          className={classes.shareButton}
          onClick={() => handleFacebookShare()}
        >
          <ResponsiveImage
            className={classes.image}
            src={FacebookIcon}
            alt="Share on Facebook"
            width={94}
            widthMobile={48}
          />
          <Typography variant="caption" className={classes.name}>
            Facebook
          </Typography>
        </FacebookShareButton>
      )}

      {isMobileOnly && (
        <Link href={`sms:?&body=${url}`} className={classes.shareButton} onClick={() => handleSmsShare()}>
          <ResponsiveImage className={classes.image} src={SMSIcon} alt="Share by SMS" width={94} widthMobile={48} />
          <Typography variant="caption" className={classes.name}>
            Text
          </Typography>
        </Link>
      )}

      <button
        type="button"
        className={classes.shareButton}
        onClick={() => onEmailFormOpen()}
        data-testid="shareoptions-email"
      >
        <ResponsiveImage className={classes.image} src={EmailIcon} alt="Share by Email" width={94} widthMobile={48} />
        <Typography variant="caption" className={classes.name}>
          Email
        </Typography>
      </button>

      <button
        type="button"
        className={classes.shareButton}
        onClick={() => handleCopyToClipboard()}
        data-testid="shareoptions-copylink"
      >
        <ResponsiveImage className={classes.image} src={LinkIcon} alt="Copy Link" width={94} widthMobile={48} />
        <Typography variant="caption" className={classes.name}>
          Copy Link
        </Typography>
      </button>

      {isWebShareSupported && !isDesktop && (
        <button type="button" className={classes.shareButton} onClick={() => handleShare()}>
          <ResponsiveImage
            className={classes.image}
            src={MoreIcon}
            alt="More share options"
            width={94}
            widthMobile={48}
          />
          <Typography variant="caption" className={classes.name}>
            More
          </Typography>
        </button>
      )}
    </div>
  );
};

ShareOptions.propTypes = {
  onEmailFormOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  make: MakePresenter.shape().isRequired,
  autoShowCVPModal: PropTypes.func.isRequired,
  message: PropTypes.shape({
    text: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
  templateGroup: TemplateGroupPresenter.shape().isRequired,
};

export default ShareOptions;
